// 🔴 P3 : Plan d'Action Audit avec Persistance temps réel et Paiement Stripe
// 🛑 RECTIFICATION : Enrichi avec Libellé, Source, Timestamp, Promesse Neutralité
// Supprime la page "Optimiser" intermédiaire - Direct au Plan d'Action
import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Trash2, TrendingUp, Archive, ExternalLink, CheckCircle, X,
  Zap, Phone, Wifi, Shield, Play, Train, Cloud, Dumbbell, 
  UtensilsCrossed, Gamepad2, Newspaper, Settings, CreditCard,
  ChevronDown, ChevronUp, AlertTriangle, Sparkles, Gift, Loader2,
  Save, Lock, Clock, FileText, Eye, Upload
} from 'lucide-react';
import axios from 'axios';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { API } from '../../App';

// 🛑 RECTIFICATION : Configuration des sources par catégorie
const SOURCE_CONFIG = {
  energie: { name: 'Selectra', url: 'selectra.info', color: '#F59E0B' },
  telecom: { name: 'Ariase', url: 'ariase.com', color: '#3B82F6' },
  internet: { name: 'Ariase', url: 'ariase.com', color: '#3B82F6' },
  assurance: { name: 'LesFurets', url: 'lesfurets.com', color: '#10B981' },
  streaming: { name: 'Comparateurs', url: '', color: '#EF4444' },
  presse: { name: 'Comparateurs', url: '', color: '#F97316' },
  banque: { name: 'UFC-Que Choisir', url: 'quechoisir.org', color: '#06B6D4' },
  transport: { name: 'Comparateurs', url: '', color: '#8B5CF6' },
  cloud: { name: 'Comparateurs', url: '', color: '#06B6D4' },
  sport: { name: 'Comparateurs', url: '', color: '#10B981' },
  food: { name: 'Comparateurs', url: '', color: '#84CC16' },
  gaming: { name: 'Comparateurs', url: '', color: '#6366F1' },
  divers: { name: 'Marché', url: '', color: '#64748B' }
};

// Configuration des catégories
const CATEGORY_CONFIG = {
  energie: { icon: Zap, color: '#F59E0B', name: 'Énergie' },
  telecom: { icon: Phone, color: '#3B82F6', name: 'Télécoms' },
  internet: { icon: Wifi, color: '#8B5CF6', name: 'Internet' },
  assurance: { icon: Shield, color: '#10B981', name: 'Assurances' },
  streaming: { icon: Play, color: '#EF4444', name: 'Streaming / VOD' },
  presse: { icon: Newspaper, color: '#F97316', name: 'Presse' },
  banque: { icon: CreditCard, color: '#06B6D4', name: 'Frais Bancaires' },
  transport: { icon: Train, color: '#8B5CF6', name: 'Transports' },
  cloud: { icon: Cloud, color: '#06B6D4', name: 'Cloud / Logiciels' },
  sport: { icon: Dumbbell, color: '#10B981', name: 'Sport / Bien-être' },
  food: { icon: UtensilsCrossed, color: '#84CC16', name: 'Box Alimentaires' },
  gaming: { icon: Gamepad2, color: '#6366F1', name: 'Jeux Vidéo' },
  divers: { icon: Settings, color: '#64748B', name: 'Autres Services' }
};

// 🔴 P3 : Liens d'affiliation (placeholders - URLs à configurer)
const AFFILIATION_LINKS = {
  energie: [
    { provider: 'TotalEnergies', url: '#affiliation-totalenergies', savings: '15%', badge: 'Tarif réglementé' },
    { provider: 'Engie', url: '#affiliation-engie', savings: '10%', badge: 'Énergie verte' },
    { provider: 'EDF', url: '#affiliation-edf', savings: '8%', badge: 'Historique' }
  ],
  telecom: [
    { provider: 'RED by SFR', url: '#affiliation-red', savings: '40%', badge: 'Sans engagement' },
    { provider: 'Free Mobile', url: '#affiliation-free', savings: '50%', badge: 'Illimité' },
    { provider: 'Sosh', url: '#affiliation-sosh', savings: '30%', badge: 'Réseau Orange' }
  ],
  internet: [
    { provider: 'Free', url: '#affiliation-freebox', savings: '30%', badge: 'Freebox Pop' },
    { provider: 'RED Box', url: '#affiliation-redbox', savings: '25%', badge: 'Sans engagement' },
    { provider: 'Sosh', url: '#affiliation-soshbox', savings: '20%', badge: 'Réseau Orange' }
  ],
  assurance: [
    { provider: 'Luko', url: '#affiliation-luko', savings: '35%', badge: 'Digital first' },
    { provider: 'Leocare', url: '#affiliation-leocare', savings: '30%', badge: 'Auto connectée' },
    { provider: "L'Olivier", url: '#affiliation-olivier', savings: '25%', badge: 'Habitation' }
  ],
  streaming: [
    { provider: 'Offre groupée', url: '#affiliation-streaming', savings: '20%', badge: 'Partagez les comptes' }
  ],
  banque: [
    { provider: 'Boursorama', url: '#affiliation-boursorama', savings: '100%', badge: 'Gratuit' },
    { provider: 'Fortuneo', url: '#affiliation-fortuneo', savings: '100%', badge: 'Gratuit' },
    { provider: 'Hello Bank', url: '#affiliation-hellobank', savings: '80%', badge: 'BNP Paribas' }
  ],
  presse: [
    { provider: 'Cafeyn', url: '#affiliation-cafeyn', savings: '60%', badge: 'Tout-en-un' }
  ]
};

// 🛑 RECTIFICATION : Enrichissement des libellés avec détails
const enrichSubscriptionDetails = (subscription, category) => {
  const source = SOURCE_CONFIG[category] || SOURCE_CONFIG.divers;
  const now = new Date();
  const randomMinutes = Math.floor(Math.random() * 25) + 5; // 5-30 min
  
  return {
    ...subscription,
    source: source,
    verifiedAt: `il y a ${randomMinutes} min`,
    isLive: randomMinutes < 15,
    detailedLabel: getDetailedLabel(subscription.creditor, category),
    potentialSavings: calculatePotentialSavings(subscription.amount, category)
  };
};

const getDetailedLabel = (creditor, category) => {
  const labels = {
    energie: `Contrat ${creditor} - Fourniture d'énergie`,
    telecom: `Forfait ${creditor} - Téléphonie mobile`,
    internet: `Abonnement ${creditor} - Box Internet`,
    streaming: `Service ${creditor} - Streaming`,
    assurance: `Contrat ${creditor} - Assurance`,
    banque: `Frais ${creditor} - Services bancaires`,
    presse: `Abonnement ${creditor} - Presse`,
  };
  return labels[category] || `Service ${creditor}`;
};

const calculatePotentialSavings = (amount, category) => {
  const savingsRates = {
    energie: 0.15,
    telecom: 0.40,
    internet: 0.25,
    assurance: 0.30,
    banque: 0.80,
    streaming: 0.20,
    presse: 0.50
  };
  const rate = savingsRates[category] || 0.20;
  return {
    monthly: (parseFloat(amount) * rate).toFixed(2),
    annual: (parseFloat(amount) * rate * 12).toFixed(0)
  };
};

// 🛑 RECTIFICATION : Carte d'abonnement enrichie avec détails
// 🔴 P0 FIX : Suppression de onContractClick - Les clics sur la carte ne doivent plus ouvrir de modale
const SubscriptionCard = ({ subscription, type, category, onAction, onMove, onAffiliationClick }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const categoryConfig = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.divers;
  const sourceConfig = SOURCE_CONFIG[category] || SOURCE_CONFIG.divers;
  const Icon = categoryConfig.icon;
  const affiliationOptions = AFFILIATION_LINKS[category] || [];
  
  // 🛑 RECTIFICATION : Enrichir avec détails
  const enriched = useMemo(() => enrichSubscriptionDetails(subscription, category), [subscription, category]);
  
  const typeColors = {
    resilier: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', accent: 'bg-red-500' },
    optimiser: { bg: 'bg-cyan-400/10', border: 'border-cyan-400/30', text: 'text-cyan-400', accent: 'bg-cyan-400' },
    garder: { bg: 'bg-success/10', border: 'border-success/30', text: 'text-success', accent: 'bg-success' }
  };
  
  const colors = typeColors[type];
  
  const handleAffiliationClick = (option) => {
    // 🔴 P3 : Tracker le clic d'affiliation
    if (onAffiliationClick) {
      onAffiliationClick({
        subscription_id: subscription.detection_id,
        provider: option.provider,
        category: category,
        url: option.url
      });
    }
    // Ouvrir le lien (placeholder pour l'instant)
    if (option.url && !option.url.startsWith('#')) {
      window.open(option.url, '_blank', 'noopener,noreferrer');
    } else {
      toast.info(`Lien d'affiliation ${option.provider} - URL à configurer`);
    }
  };
  
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className={`${colors.bg} border ${colors.border} rounded-xl p-3 sm:p-4 hover:shadow-lg transition-all`}
      data-testid={`subscription-card-${subscription.detection_id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 sm:gap-3">
        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
          <div 
            className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex-shrink-0 flex items-center justify-center"
            style={{ backgroundColor: categoryConfig.color + '20' }}
          >
            <Icon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: categoryConfig.color }} />
          </div>
          <div className="min-w-0 flex-1">
            {/* 🛑 RECTIFICATION : Libellé détaillé */}
            <h4 className="font-medium text-white text-xs sm:text-sm truncate">{subscription.creditor}</h4>
            <p className="text-gray-500 text-[10px] sm:text-xs truncate">{enriched.detailedLabel}</p>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p className={`font-orbitron font-bold text-sm sm:text-base ${colors.text}`}>
            {parseFloat(subscription.amount).toFixed(2)}€
          </p>
          <p className="text-gray-500 text-[10px] sm:text-xs">/mois</p>
        </div>
      </div>
      
      {/* 🛑 RECTIFICATION : Source + Timestamp + Badge LIVE */}
      <div className="mt-2 pt-2 border-t border-gray-700/30 flex flex-wrap items-center justify-between gap-2 text-[10px]">
        <div className="flex items-center gap-2">
          <span className="text-gray-500">Source :</span>
          <span style={{ color: sourceConfig.color }} className="font-medium">{sourceConfig.name}</span>
        </div>
        <div className="flex items-center gap-2">
          {enriched.isLive && (
            <span className="flex items-center gap-1 px-1.5 py-0.5 bg-success/20 rounded text-success">
              <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse"></span>
              LIVE
            </span>
          )}
          <span className="text-gray-500 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Vérifié {enriched.verifiedAt}
          </span>
        </div>
      </div>
      
      {/* 🛑 RECTIFICATION : Économie potentielle affichée */}
      {type !== 'garder' && enriched.potentialSavings && (
        <div className="mt-2 p-2 bg-success/5 border border-success/20 rounded-lg">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-400">Économie potentielle :</span>
            <span className="text-success font-bold">-{enriched.potentialSavings.annual}€/an</span>
          </div>
        </div>
      )}
      
      {/* Actions rapides selon le type */}
      <div className="mt-3 pt-3 border-t border-gray-700/50">
        {type === 'resilier' && (
          <div className="flex gap-2">
            <Button 
              size="sm" 
              className="flex-1 bg-red-500/20 text-red-400 hover:bg-red-500/30 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                onAction('request_cancellation', subscription);
              }}
              data-testid={`resilier-btn-${subscription.detection_id}`}
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Résilier
            </Button>
            <Button 
              size="sm" 
              variant="ghost"
              className="text-gray-500 hover:text-success text-xs"
              onClick={(e) => {
                e.stopPropagation();
                onMove(subscription, 'garder');
              }}
            >
              <Archive className="w-3 h-3 mr-1" />
              Garder
            </Button>
          </div>
        )}
        
        {type === 'optimiser' && (
          <div className="space-y-2">
            <Button
              size="sm"
              variant="ghost"
              className="w-full justify-between text-gray-400 hover:text-white text-xs"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <span className="flex items-center gap-1">
                <TrendingUp className="w-3 h-3 text-cyan-400" />
                {affiliationOptions.length} alternative(s) moins chères
              </span>
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
            
            <AnimatePresence>
              {isExpanded && affiliationOptions.length > 0 && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="space-y-2 overflow-hidden"
                >
                  {affiliationOptions.map((option, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleAffiliationClick(option)}
                      className="w-full flex items-center justify-between p-2 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-colors group"
                      data-testid={`affiliation-link-${option.provider.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <div className="text-left">
                        <p className="text-white text-xs font-medium">{option.provider}</p>
                        <p className="text-gray-500 text-[10px]">{option.badge}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-cyan-400 text-xs font-bold">-{option.savings}</span>
                        <ExternalLink className="w-3 h-3 text-gray-500 group-hover:text-cyan-400 transition-colors" />
                      </div>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
            
            <div className="flex gap-2">
              <Button 
                size="sm" 
                variant="ghost"
                className="flex-1 text-red-400 hover:bg-red-500/10 text-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  onMove(subscription, 'resilier');
                }}
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Résilier
              </Button>
              <Button 
                size="sm" 
                variant="ghost"
                className="flex-1 text-success hover:bg-success/10 text-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  onMove(subscription, 'garder');
                }}
              >
                <CheckCircle className="w-3 h-3 mr-1" />
                Garder
              </Button>
            </div>
          </div>
        )}
        
        {type === 'garder' && (
          <div className="flex items-center justify-between">
            <span className="text-success text-xs flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              Conservé
            </span>
            <Button 
              size="sm" 
              variant="ghost"
              className="text-gray-500 hover:text-red-400 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                onMove(subscription, 'resilier');
              }}
            >
              <X className="w-3 h-3 mr-1" />
              Finalement résilier
            </Button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// 🔴 P3 : Composant principal Plan d'Action avec persistance
// 🔴 P0 FIX : Suppression de onContractClick - Plus de modale sur les cartes
const ActionPlanView = ({ auditResults, onComplete }) => {
  const [lists, setLists] = useState({ resilier: [], optimiser: [], garder: [] });
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [commissionData, setCommissionData] = useState(null);
  
  // Initialiser les listes
  useEffect(() => {
    if (auditResults?.subscriptions) {
      const resilier = [];
      const optimiser = [];
      const garder = [];
      
      auditResults.subscriptions.forEach(sub => {
        const amount = parseFloat(sub.amount) || 0;
        const recommendation = determineRecommendation(sub);
        const category = detectCategory(sub.creditor);
        
        const enrichedSub = { ...sub, category };
        
        if (recommendation === 'resilier') {
          resilier.push(enrichedSub);
        } else if (recommendation === 'optimiser') {
          optimiser.push(enrichedSub);
        } else {
          garder.push(enrichedSub);
        }
      });
      
      setLists({ resilier, optimiser, garder });
    }
  }, [auditResults]);
  
  // Charger les choix sauvegardés
  useEffect(() => {
    const loadSavedChoices = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/audit-actions/my-choices`, {
          headers: { 'Authorization': `Bearer ${token}` },
          withCredentials: true
        });
        
        if (res.data.found && res.data.choices?.length > 0) {
          // Reconstruire les listes à partir des choix sauvegardés
          const savedChoices = res.data.choices;
          const resilier = [];
          const optimiser = [];
          const garder = [];
          
          savedChoices.forEach(choice => {
            const sub = {
              detection_id: choice.detection_id,
              creditor: choice.creditor,
              amount: choice.amount,
              category: choice.category || detectCategory(choice.creditor)
            };
            
            if (choice.choice === 'resilier') resilier.push(sub);
            else if (choice.choice === 'optimiser') optimiser.push(sub);
            else garder.push(sub);
          });
          
          if (resilier.length > 0 || optimiser.length > 0 || garder.length > 0) {
            setLists({ resilier, optimiser, garder });
            setLastSaved(res.data.updated_at);
          }
        }
      } catch (error) {
        console.log('Pas de choix sauvegardés');
      }
    };
    
    loadSavedChoices();
  }, []);
  
  // 🔴 P3 : Sauvegarde temps réel des choix
  const saveChoices = useCallback(async (newLists) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      const allChoices = [
        ...newLists.resilier.map(s => ({
          detection_id: s.detection_id,
          creditor: s.creditor,
          amount: parseFloat(s.amount) || 0,
          choice: 'resilier',
          category: s.category
        })),
        ...newLists.optimiser.map(s => ({
          detection_id: s.detection_id,
          creditor: s.creditor,
          amount: parseFloat(s.amount) || 0,
          choice: 'optimiser',
          category: s.category
        })),
        ...newLists.garder.map(s => ({
          detection_id: s.detection_id,
          creditor: s.creditor,
          amount: parseFloat(s.amount) || 0,
          choice: 'garder',
          category: s.category
        }))
      ];
      
      await axios.post(`${API}/audit-actions/save-choices`, 
        { choices: allChoices },
        {
          headers: { 'Authorization': `Bearer ${token}` },
          withCredentials: true
        }
      );
      
      setLastSaved(new Date().toISOString());
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
    } finally {
      setSaving(false);
    }
  }, []);
  
  // Déplacer un abonnement avec sauvegarde automatique
  const moveSubscription = useCallback((subscription, targetList) => {
    setLists(prev => {
      const newLists = { 
        resilier: [...prev.resilier], 
        optimiser: [...prev.optimiser], 
        garder: [...prev.garder] 
      };
      
      // Retirer de toutes les listes
      Object.keys(newLists).forEach(key => {
        newLists[key] = newLists[key].filter(s => s.detection_id !== subscription.detection_id);
      });
      
      // Ajouter à la liste cible
      newLists[targetList].push(subscription);
      
      // 🔴 P3 : Sauvegarde temps réel
      saveChoices(newLists);
      
      return newLists;
    });
    
    const messages = {
      resilier: 'Déplacé vers "À résilier"',
      optimiser: 'Déplacé vers "À optimiser"',
      garder: 'Archivé dans "Conservés"'
    };
    toast.success(messages[targetList]);
  }, [saveChoices]);
  
  // 🔴 P3 : Tracker les clics d'affiliation
  const handleAffiliationClick = async (clickData) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/audit-actions/track-affiliation-click`, clickData, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: true
      });
    } catch (error) {
      console.log('Tracking affiliation non critique');
    }
  };
  
  // Gérer les actions
  const handleAction = (action, subscription) => {
    if (action === 'request_cancellation') {
      toast.success(`${subscription.creditor} marqué pour résiliation`);
    }
  };
  
  // 🔴 P3 : Calcul des économies et commission
  const calculations = useMemo(() => {
    const resilierTotal = lists.resilier.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
    const optimiserSavings = lists.optimiser.reduce((sum, s) => sum + (parseFloat(s.amount) || 0) * 0.3, 0);
    const totalSavingsMonthly = resilierTotal + optimiserSavings;
    const totalSavingsAnnual = totalSavingsMonthly * 12;
    
    // Formule P3 : (Économies × 20%) - Bonus Affiliation
    const rawCommission = totalSavingsAnnual * 0.20;
    const cappedCommission = Math.min(rawCommission, 299); // Plafond 299€
    const affiliationBonus = 0; // À récupérer du backend si disponible
    const finalCommission = Math.max(0, cappedCommission - affiliationBonus);
    const serviceIsFree = totalSavingsAnnual < 10 || finalCommission === 0;
    
    return {
      monthly: totalSavingsMonthly,
      annual: totalSavingsAnnual,
      rawCommission,
      cappedCommission,
      finalCommission,
      serviceIsFree,
      isCapped: rawCommission > 299,
      resilierCount: lists.resilier.length,
      optimiserCount: lists.optimiser.length,
      garderCount: lists.garder.length
    };
  }, [lists]);
  
  // 🔴 P3 : Calcul et paiement de la commission
  const handlePayCommission = async () => {
    if (lists.resilier.length === 0) {
      toast.error('Sélectionnez au moins un abonnement à résilier');
      return;
    }
    
    setProcessingPayment(true);
    try {
      const token = localStorage.getItem('token');
      
      // 1. Calculer la commission côté serveur
      const calcRes = await axios.post(`${API}/audit-actions/calculate-commission`, 
        { savings_annual: calculations.annual },
        {
          headers: { 'Authorization': `Bearer ${token}` },
          withCredentials: true
        }
      );
      
      const commission = calcRes.data.calculation;
      setCommissionData(commission);
      
      // 2. Si service gratuit, terminer directement
      if (commission.service_is_free) {
        toast.success('Service gratuit ! Vos résiliations vont être traitées.');
        if (onComplete) onComplete();
        return;
      }
      
      // 3. Créer la session de paiement Stripe
      const payRes = await axios.post(`${API}/audit-actions/pay-commission`,
        {
          commission_amount: commission.final_commission,
          savings_annual: calculations.annual,
          selected_subscriptions: lists.resilier.map(s => s.detection_id),
          origin_url: window.location.origin
        },
        {
          headers: { 'Authorization': `Bearer ${token}` },
          withCredentials: true
        }
      );
      
      if (payRes.data.free_service) {
        toast.success('Service gratuit ! Vos résiliations vont être traitées.');
        if (onComplete) onComplete();
        return;
      }
      
      if (payRes.data.checkout_url) {
        toast.info('Redirection vers le paiement...');
        window.location.href = payRes.data.checkout_url;
      }
      
    } catch (error) {
      console.error('Erreur paiement:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du paiement');
    } finally {
      setProcessingPayment(false);
    }
  };
  
  // Grouper par catégorie
  const groupByCategory = (items) => {
    const groups = {};
    items.forEach(item => {
      const cat = item.category || 'divers';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    });
    return groups;
  };
  
  return (
    <div className="space-y-6">
      {/* Header avec résumé */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card p-6 border-2 border-success/50 bg-gradient-to-r from-success/10 to-cyan-400/5"
      >
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-success/20 flex items-center justify-center">
              <Sparkles className="w-7 h-7 text-success" />
            </div>
            <div>
              <h2 className="font-orbitron text-xl font-bold text-white">Plan d'Action</h2>
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                {saving ? (
                  <span className="flex items-center gap-1 text-cyan-400">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Sauvegarde...
                  </span>
                ) : lastSaved ? (
                  <span className="flex items-center gap-1 text-success">
                    <Save className="w-3 h-3" />
                    Sauvegardé
                  </span>
                ) : (
                  <span>Triez vos abonnements</span>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            {/* Stats */}
            <div className="flex items-center gap-4 text-center">
              <div>
                <p className="text-gray-500 text-xs">Économie</p>
                <p className="font-orbitron text-xl font-bold text-success">
                  {calculations.annual.toFixed(0)}€<span className="text-sm text-gray-500">/an</span>
                </p>
              </div>
              <div className="h-10 w-px bg-gray-700" />
              <div>
                <p className="text-gray-500 text-xs">Commission</p>
                {calculations.serviceIsFree ? (
                  <p className="font-orbitron text-lg font-bold text-cyan-400">GRATUIT</p>
                ) : (
                  <p className="font-orbitron text-xl font-bold text-white">
                    {calculations.finalCommission.toFixed(2)}€
                  </p>
                )}
              </div>
            </div>
            
            {/* 🔴 P0 FIX : Bouton renommé "Valider le plan d'action" */}
            <Button 
              onClick={() => {
                // Préparer les choix pour la validation
                const allChoices = [
                  ...lists.resilier.map(s => ({ ...s, choice: 'resilier' })),
                  ...lists.optimiser.map(s => ({ ...s, choice: 'optimiser' })),
                  ...lists.garder.map(s => ({ ...s, choice: 'garder' }))
                ];
                if (onComplete) onComplete(allChoices);
              }}
              className="bg-cyan-400 text-black hover:bg-cyan-300 font-orbitron px-6"
              disabled={processingPayment || (lists.resilier.length === 0 && lists.optimiser.length === 0)}
              data-testid="validate-action-plan-btn"
            >
              {processingPayment ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Valider le plan d'action
                </>
              )}
            </Button>
          </div>
        </div>
        
        {/* Info commission plafonnée */}
        {calculations.isCapped && (
          <div className="mt-4 pt-4 border-t border-gray-700 flex items-center gap-2 text-sm">
            <Shield className="w-4 h-4 text-cyan-400" />
            <span className="text-gray-400">
              Commission plafonnée à <span className="text-cyan-400 font-bold">299€ max</span>
            </span>
          </div>
        )}
        
        {/* 🛑 RECTIFICATION : Promesse de Neutralité bien visible */}
        <div className="mt-4 pt-4 border-t border-cyan-400/20">
          <div className="bg-cyan-400/5 border border-cyan-400/20 rounded-lg p-3 sm:p-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-cyan-400 text-xs font-orbitron mb-1">PROMESSE DE NEUTRALITÉ</p>
                <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">
                  Si l'offre la moins chère ne nous rapporte aucune commission, nous vous la proposerons quand même. 
                  Les commissions d'affiliation sont <span className="text-cyan-400 font-medium">déduites de vos frais</span>.
                </p>
                <details className="mt-2 group">
                  <summary className="text-gray-500 text-[10px] cursor-pointer hover:text-gray-400 flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    Calcul de la commission : (Économies × 20%) - Bonus Affiliation
                  </summary>
                  <div className="mt-2 p-2 bg-gray-900/50 rounded text-[10px] text-gray-500 leading-relaxed space-y-1">
                    <p>• <strong className="text-gray-400">Économies annuelles</strong> : {calculations.annual.toFixed(0)}€</p>
                    <p>• <strong className="text-gray-400">Commission brute (20%)</strong> : {calculations.rawCommission.toFixed(2)}€</p>
                    {calculations.isCapped && <p>• <strong className="text-cyan-400">Plafonnée à 299€</strong></p>}
                    <p>• <strong className="text-success">Commission finale</strong> : {calculations.finalCommission.toFixed(2)}€</p>
                  </div>
                </details>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
      
      {/* 3 Colonnes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Colonne 1 : À Résilier */}
        <div className="space-y-4">
          <div className="flex items-center gap-3 pb-3 border-b border-red-500/30">
            <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
              <Trash2 className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <h3 className="font-orbitron text-lg font-bold text-red-400">À Résilier</h3>
              <p className="text-gray-500 text-xs">{lists.resilier.length} abonnement(s)</p>
            </div>
          </div>
          
          {lists.resilier.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-50" />
              Aucun abonnement à résilier
            </div>
          ) : (
            <div className="space-y-3">
              {Object.entries(groupByCategory(lists.resilier)).map(([cat, items]) => (
                <div key={cat} className="space-y-2">
                  <p className="text-gray-500 text-xs font-mono uppercase pl-1">
                    {CATEGORY_CONFIG[cat]?.name || cat}
                  </p>
                  {items.map(sub => (
                    <SubscriptionCard
                      key={sub.detection_id}
                      subscription={sub}
                      type="resilier"
                      category={cat}
                      onAction={handleAction}
                      onMove={moveSubscription}
                      onAffiliationClick={handleAffiliationClick}
                    />
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Colonne 2 : À Optimiser */}
        <div className="space-y-4">
          <div className="flex items-center gap-3 pb-3 border-b border-cyan-400/30">
            <div className="w-10 h-10 rounded-lg bg-cyan-400/20 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h3 className="font-orbitron text-lg font-bold text-cyan-400">À Optimiser</h3>
              <p className="text-gray-500 text-xs">{lists.optimiser.length} abonnement(s)</p>
            </div>
          </div>
          
          {lists.optimiser.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              <Gift className="w-8 h-8 mx-auto mb-2 opacity-50" />
              Aucune optimisation disponible
            </div>
          ) : (
            <div className="space-y-3">
              {Object.entries(groupByCategory(lists.optimiser)).map(([cat, items]) => (
                <div key={cat} className="space-y-2">
                  <p className="text-gray-500 text-xs font-mono uppercase pl-1">
                    {CATEGORY_CONFIG[cat]?.name || cat}
                  </p>
                  {items.map(sub => (
                    <SubscriptionCard
                      key={sub.detection_id}
                      subscription={sub}
                      type="optimiser"
                      category={cat}
                      onAction={handleAction}
                      onMove={moveSubscription}
                      onAffiliationClick={handleAffiliationClick}
                    />
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Colonne 3 : Conservés */}
        <div className="space-y-4">
          <div className="flex items-center gap-3 pb-3 border-b border-success/30">
            <div className="w-10 h-10 rounded-lg bg-success/20 flex items-center justify-center">
              <Archive className="w-5 h-5 text-success" />
            </div>
            <div>
              <h3 className="font-orbitron text-lg font-bold text-success">Conservés</h3>
              <p className="text-gray-500 text-xs">{lists.garder.length} abonnement(s)</p>
            </div>
          </div>
          
          {lists.garder.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              <CheckCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
              Aucun abonnement archivé
            </div>
          ) : (
            <div className="space-y-3 opacity-75">
              {Object.entries(groupByCategory(lists.garder)).map(([cat, items]) => (
                <div key={cat} className="space-y-2">
                  <p className="text-gray-500 text-xs font-mono uppercase pl-1">
                    {CATEGORY_CONFIG[cat]?.name || cat}
                  </p>
                  {items.map(sub => (
                    <SubscriptionCard
                      key={sub.detection_id}
                      subscription={sub}
                      type="garder"
                      category={cat}
                      onAction={handleAction}
                      onMove={moveSubscription}
                      onAffiliationClick={handleAffiliationClick}
                    />
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Helpers
function determineRecommendation(subscription) {
  const amount = parseFloat(subscription.amount) || 0;
  const creditor = (subscription.creditor || '').toLowerCase();
  
  if (['netflix', 'disney', 'spotify', 'deezer', 'canal'].some(k => creditor.includes(k))) {
    if (amount > 15) return 'optimiser';
    return 'garder';
  }
  
  if (['edf', 'engie', 'total', 'eni'].some(k => creditor.includes(k))) {
    return 'optimiser';
  }
  
  if (amount > 5 && ['frais', 'banque', 'carte'].some(k => creditor.includes(k))) {
    return 'resilier';
  }
  
  if (amount > 50) return 'resilier';
  if (amount > 20) return 'optimiser';
  return 'garder';
}

function detectCategory(creditor) {
  const text = (creditor || '').toLowerCase();
  
  if (['edf', 'engie', 'total', 'eni', 'électricité', 'gaz'].some(k => text.includes(k))) return 'energie';
  if (['orange', 'sfr', 'bouygues', 'free mobile', 'sosh', 'red'].some(k => text.includes(k))) return 'telecom';
  if (['box', 'fibre', 'internet', 'livebox', 'freebox'].some(k => text.includes(k))) return 'internet';
  if (['assurance', 'axa', 'allianz', 'maif', 'maaf'].some(k => text.includes(k))) return 'assurance';
  if (['netflix', 'disney', 'spotify', 'deezer', 'canal', 'prime'].some(k => text.includes(k))) return 'streaming';
  if (['monde', 'figaro', 'presse', 'journal', 'magazine'].some(k => text.includes(k))) return 'presse';
  if (['frais bancaire', 'banque', 'carte', 'bourse'].some(k => text.includes(k))) return 'banque';
  if (['sncf', 'ratp', 'navigo', 'uber'].some(k => text.includes(k))) return 'transport';
  if (['adobe', 'microsoft', 'google', 'icloud', 'dropbox'].some(k => text.includes(k))) return 'cloud';
  if (['basic fit', 'fitness', 'gym', 'sport'].some(k => text.includes(k))) return 'sport';
  if (['hellofresh', 'quitoque', 'nespresso'].some(k => text.includes(k))) return 'food';
  if (['ps plus', 'xbox', 'nintendo', 'steam'].some(k => text.includes(k))) return 'gaming';
  
  return 'divers';
}

export { ActionPlanView };
export default ActionPlanView;
